import { apiClient } from './client';

/**
 * 내 임금 · 가산 시간 (시안 B9).
 *
 * 서버: `apps/chat-server/src/modules/hr/my-wage.routes.ts`.
 *
 * ★★ **통상임금 금액은 오지 않는다.** 시급·월 통상임금은 급여 담당자의 것이고, 직원에게
 *    보이는 것은 **시간과 예상 수당까지**다. 임금 정보는 «등록됐다는 사실» 만 온다.
 * ★★ **`amount === null` 은 0 이 아니다** — 임금이 등록되지 않아 «계산할 수 없다» 는 뜻이다.
 *    화면이 둘을 같게 그리면 「일했는데 0원」 으로 읽힌다.
 * ★ 가산 셋(연장 · 휴일 · 야간)은 **겹친다**. 더하면 없는 시간이 생긴다 —
 *   합은 서버가 낸 `estimate.countedMinutes` 를 쓴다.
 */

export interface PremiumMinutesDto {
  overtimeMinutes: number;
  holidayMinutes: number;
  holidayOverMinutes: number;
  /** ★ 연장 · 휴일과 **겹친다**. */
  nightMinutes: number;
}

export interface MyWageSpanDto {
  /** `YYYY-MM-DD`. */
  date: string;
  start: string;
  end: string;
  minutes: number;
  holiday: boolean;
  reason: string | null;
}

export interface MyWageEstimateDto {
  /** ★ null 은 «계산 불가»(임금 미등록) — 0 이 아니다. */
  amount: number | null;
  reason: string;
  /** 겹침을 걷어낸 정산 기준 시간(분). */
  countedMinutes: number;
  multiplierNote: string;
}

export interface MyWageInfoDto {
  registered: boolean;
  wageType: string | null;
  wageTypeLabel: string | null;
  /** 약정 시간(분). */
  monthlyStandardMinutes: number | null;
  effectiveFrom: string | null;
}

export interface MyWageTrendDto {
  year: number;
  month: number;
  premium: PremiumMinutesDto;
  amount: number | null;
}

export interface MyWageDto {
  year: number;
  month: number;
  premium: PremiumMinutesDto;
  spans: MyWageSpanDto[];
  estimate: MyWageEstimateDto;
  wage: MyWageInfoDto;
  trend: MyWageTrendDto[];
  basis: { nightWindow: string; holidayDates: number; weeklyRestCounted: boolean };
}

export async function fetchMyWage(year?: number, month?: number): Promise<MyWageDto> {
  const { data } = await apiClient.get<MyWageDto>('/hr/my-wage', {
    params: year && month ? { year, month } : undefined,
  });
  return { ...data, spans: Array.isArray(data?.spans) ? data.spans : [], trend: Array.isArray(data?.trend) ? data.trend : [] };
}

/** 분을 «2.5h» 로. 시간은 소수 첫째 자리까지 — 분 단위는 사람이 못 읽는다. */
export function hoursText(minutes: number): string {
  return `${(minutes / 60).toFixed(1)}h`;
}

/**
 * 이 구간이 **야간(22:00–06:00)에 걸치나** — 표식을 붙일지 판정한다.
 *
 * ★ 야간은 **별도 구분이 아니라 겹치는 가산**이다(연장이면서 야간일 수 있다). 그래서
 *   구분을 «야간» 으로 바꾸지 않고 표식을 **더한다** — 바꾸면 연장 근로였다는 사실이 사라진다.
 * ★ 자정을 넘는 구간(23:00–01:00)은 끝이 시작보다 작다. 그때는 넘어간 것으로 보고,
 *   자정 이후는 늘 야간 안이다.
 */
export function touchesNight(start: string, end: string): boolean {
  const m = (t: string) => Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5));
  const s0 = m(start);
  const e0 = m(end);
  const NIGHT_FROM = 22 * 60;
  const NIGHT_TO = 6 * 60;
  if (e0 <= s0) return true;              // 자정을 넘겼다 — 반드시 야간에 걸친다
  return s0 < NIGHT_TO || e0 > NIGHT_FROM;
}
